import { Platform } from 'react-native';
import { API_HOST } from '@/constants/config';

// Shared network chokepoint for every API slice (Perf 3.1.e — store/API split).
// This module is a LEAF: it must not import any `@/lib/api` slice, so slices can
// pull just `fetchWithAuth`/`ApiError` without dragging in the whole monolith.

// Lazy import to avoid circular dependency with the store.
// The store imports the API, and the API needs the store. Points at the auth
// leaf (store/auth.js) directly so this chokepoint doesn't reach for the whole
// store barrel (Perf 3.1.b Step 3).
const getAuthStore = () => require('@/store/auth').useAuthStore;

// Default timeout for API requests (30 seconds)
const DEFAULT_TIMEOUT_MS = 30000;

// Max time to wait for the persisted auth store to rehydrate before giving up.
const AUTH_HYDRATION_TIMEOUT_MS = 2000;
const TRANSIENT_RETRY_DELAY_MS = 250;

function shouldRetryTransientRequest(method, retryCount) {
  const normalizedMethod = String(method || 'GET').toUpperCase();
  return retryCount > 0 && ['GET', 'HEAD', 'OPTIONS', 'PUT'].includes(normalizedMethod);
}

/**
 * Wait for the persisted auth store to finish rehydrating.
 *
 * On native the auth store is backed by AsyncStorage, which hydrates
 * ASYNCHRONOUSLY — there is a window right after launch / after an OTA reload
 * where `state.token` is still the default `null` even for a logged-in user.
 * On web (synchronous localStorage) this window doesn't exist. Without this
 * gate, any personal request (`/json/files`, `/json/collections`, `/json/memory`,
 * …) that fires during that window hits the `NO_AUTH_TOKEN` short-circuit
 * below and fails *silently* (it's marked `silent`), so the screen renders
 * empty with no error and no retry. Awaiting hydration here fixes that class
 * of bug at the single network chokepoint rather than per-screen.
 *
 * No-ops (resolves immediately) once hydration has completed, so steady-state
 * requests pay no cost.
 */
async function awaitAuthHydration() {
  const store = getAuthStore();
  const persistApi = store?.persist;
  // No persist API (or already hydrated) → nothing to wait for.
  if (!persistApi || typeof persistApi.hasHydrated !== 'function' || persistApi.hasHydrated()) {
    return;
  }
  await new Promise((resolve) => {
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    // Safety timeout: never block a request indefinitely if hydration stalls.
    const timer = setTimeout(finish, AUTH_HYDRATION_TIMEOUT_MS);
    const unsub = persistApi.onFinishHydration?.(() => {
      clearTimeout(timer);
      unsub?.();
      finish();
    });
    // Re-check synchronously in case hydration completed between the guard
    // above and subscribing.
    if (persistApi.hasHydrated()) {
      clearTimeout(timer);
      unsub?.();
      finish();
    }
  });
}

class ApiError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

/**
 * JSON routes that always require a Bearer token. When unauthenticated, we
 * fail fast with a `silent: true` 401 instead of hitting the API — avoids
 * noisy 401 / "No token provided" responses for anon visitors and lets
 * `addToast` swallow the resulting `ApiError`.
 *
 * Public dashboard rollout (Phase 8): the prefix list grew to cover every
 * personal endpoint surfaced in `docs/PUBLIC_DASHBOARD_TODO.md`. Public
 * read endpoints (agent/tool/model/deck/task-template catalogs, explore
 * feed, public conversation reads at `/json/conversations/:id/detail`,
 * etc.) are intentionally *not* listed here.
 */
const AUTH_REQUIRED_PREFIXES = [
  '/json/files',
  '/json/collections',
  '/json/bookmarks',
  '/json/bookmark-folders',
  '/json/memory',
  '/json/user/me',
  '/json/user/track',
  '/json/tool-runs',
  '/json/inference-tasks',
  '/json/decks',
  '/json/account',
  '/json/auth/logout',
  '/json/agents/recent',
  '/json/agents/my',
  '/json/agents/new',
  '/json/models/frequent',
  '/json/conversations/new',
  '/json/conversations/calculate-credits',
  '/json/conversations/bulk-archive',
  '/json/conversations/bulk-unarchive',
  '/json/conversations/bulk-delete',
  '/json/conversations/existing-ids',
  '/json/conversations/search',
];

function endpointRequiresAuthToken(endpoint) {
  const path = String(endpoint || '').split('?')[0];
  if (AUTH_REQUIRED_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
    return true;
  }
  // Tool mutations (run, credit estimate, magic-wand suggestions) require login.
  return /\/json\/(tools|prompts)\/[^/]+\/(enhance-field|run|calculate-credits)$/.test(path);
}

/**
 * True if the error is the "anonymous user hit an auth-required endpoint"
 * sentinel raised by `fetchWithAuth`. Callers can use this to skip user-
 * facing toasts since the auth CTA flow is the source of truth.
 *
 *   } catch (err) {
 *     if (isAnonAuthError(err)) return;
 *     addToast(err.message, 'error');
 *   }
 */
function isAnonAuthError(err) {
  return !!(err && err.status === 401 && err.data && (err.data.code === 'NO_AUTH_TOKEN' || err.data.silent === true));
}

/**
 * Fetch with timeout — wraps native fetch with an AbortController timeout.
 * Prevents requests from hanging indefinitely on iOS/Android.
 */
function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, {
    ...options,
    signal: controller.signal,
  }).finally(() => clearTimeout(timeoutId));
}

async function fetchWithAuth(endpoint, options = {}) {
  // Native AsyncStorage hydrates asynchronously — wait for it before reading
  // the token, otherwise a logged-in user can get a false NO_AUTH_TOKEN during
  // the post-launch / post-OTA hydration window (see awaitAuthHydration).
  await awaitAuthHydration();

  const authStore = getAuthStore().getState();
  let token = authStore.token;

  if (!token && endpointRequiresAuthToken(endpoint)) {
    // Mark `silent: true` so global error sinks (`useToastStore.addToast`,
    // store reducers) can drop the toast and rely on the auth CTA flow.
    throw new ApiError('Sign in to continue.', 401, { code: 'NO_AUTH_TOKEN', silent: true });
  }
  
  // Skip token refresh for auth endpoints that must not trigger recursive refresh
  const skipTokenRefresh = endpoint.includes('/auth/refresh-token') ||
                          endpoint.includes('/auth/logout');

  // Check if token needs refresh before making the request
  if (token && authStore.isTokenExpired() && !skipTokenRefresh) {
    try {
      token = await authStore.getValidToken();
    } catch (error) {
      // If refresh fails, clear the session and throw error
      authStore.clearSession();
      throw new ApiError('Session expired. Please log in again.', 401);
    }
  }

  const defaultHeaders = {
    'X-Platform': Platform.OS,
  };
  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  // Pull `timeoutMs` and `responseType` off of options so they aren't
  // forwarded into native fetch. `responseType: 'blob'` returns an object
  // shaped like `{ blob, contentType, filename }` instead of parsing the
  // body as JSON — used by the account-data export download which fetches
  // a binary ZIP archive.
  const { timeoutMs, responseType, networkRetries = 1, ...fetchOptions } = options;
  const wantsBlob = responseType === 'blob';

  if (fetchOptions.body && !(fetchOptions.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json';
    fetchOptions.body = JSON.stringify(fetchOptions.body);
  }

  let response;
  try {
    response = await fetchWithTimeout(`${API_HOST}${endpoint}`, {
      ...fetchOptions,
      credentials: 'include',
      headers: {
        ...defaultHeaders,
        ...fetchOptions.headers,
      },
    }, timeoutMs);
  } catch (error) {
    // Native networking occasionally reports a transient transport failure
    // while the machine is still online (for example immediately after wake).
    // Retry read requests and idempotent PUT updates once before asking the
    // user to intervene. Never retry chat/create POSTs, which could duplicate
    // side effects.
    if (shouldRetryTransientRequest(fetchOptions.method, networkRetries)) {
      await new Promise((resolve) => setTimeout(resolve, TRANSIENT_RETRY_DELAY_MS));
      return fetchWithAuth(endpoint, {
        ...options,
        networkRetries: networkRetries - 1,
      });
    }
    if (error.name === 'AbortError') {
      throw new ApiError('Request timed out. Please check your connection and try again.', 0);
    }
    // Network errors (offline, DNS failure, etc.)
    throw new ApiError(
      Platform.OS === 'web'
        ? 'Network error. Please check your connection.'
        : 'Unable to connect. Please check your internet connection and try again.',
      0
    );
  }

  // Handle 401 responses by attempting token refresh — done BEFORE parsing
  // the body so the binary blob path can also recover from a stale token.
  if (response.status === 401 && token && !skipTokenRefresh) {
    try {
      const newToken = await authStore.getValidToken();
      const retryResponse = await fetchWithTimeout(`${API_HOST}${endpoint}`, {
        ...fetchOptions,
        headers: {
          ...fetchOptions.headers,
          ...defaultHeaders,
          'Authorization': `Bearer ${newToken}`,
        },
      }, timeoutMs);

      if (!retryResponse.ok) {
        let retryErrorBody = null;
        try { retryErrorBody = await retryResponse.json(); } catch (_) { /* ignore */ }
        authStore.clearSession();
        throw new ApiError(
          retryErrorBody?.message || 'Session expired. Please log in again.',
          retryResponse.status,
          retryErrorBody,
        );
      }

      if (wantsBlob) {
        const blob = await retryResponse.blob();
        return {
          blob,
          contentType: retryResponse.headers.get('content-type') || '',
          filename: parseFilenameFromContentDisposition(retryResponse.headers.get('content-disposition')),
        };
      }
      return await retryResponse.json();
    } catch (error) {
      authStore.clearSession();
      throw new ApiError('Session expired. Please log in again.', 401);
    }
  }

  if (wantsBlob) {
    if (!response.ok) {
      // For a binary endpoint the server should still answer JSON-shaped
      // errors so we can surface a useful message. Fall back to the raw
      // status text when the body isn't JSON.
      let errorBody = null;
      try { errorBody = await response.json(); } catch (_) { /* ignore */ }
      throw new ApiError(
        errorBody?.message || `Request failed (${response.status})`,
        response.status,
        errorBody,
      );
    }
    const blob = await response.blob();
    return {
      blob,
      contentType: response.headers.get('content-type') || '',
      filename: parseFilenameFromContentDisposition(response.headers.get('content-disposition')),
    };
  }

  let data;
  try {
    data = await response.json();
  } catch (error) {
    // JSON parsing failed - server likely returned HTML or plain text.
    // Surface the status so gateway/proxy errors (502/503/504) are debuggable
    // instead of being flattened to "Invalid response from server".
    const gatewayMessages = {
      502: 'Bad gateway (502). The server is unreachable — please try again.',
      503: 'Service unavailable (503). Please try again in a moment.',
      504: 'Request timed out at the gateway (504). The model is taking longer than the proxy allows — try a smaller request or retry.',
    };
    const message = gatewayMessages[response.status]
      || (response.status >= 500
          ? `Server error (${response.status}). Please try again.`
          : 'Invalid response from server');
    throw new ApiError(message, response.status);
  }

  if (!response.ok) {
    throw new ApiError(
      data.message || 'Something went wrong',
      response.status,
      data
    );
  }

  return data;
}

// Extract `filename="..."` from a Content-Disposition header, falling back
// to null when the header is missing or doesn't carry a filename. Used by
// blob downloads to preserve the server-suggested filename.
function parseFilenameFromContentDisposition(header) {
  if (!header) return null;
  // RFC 5987 `filename*=UTF-8''...` first, then plain `filename="..."`.
  const m1 = /filename\*\s*=\s*[^']*''([^;]+)/i.exec(header);
  if (m1) {
    try { return decodeURIComponent(m1[1].trim().replace(/"/g, '')); } catch (_) { /* ignore */ }
  }
  const m2 = /filename\s*=\s*"?([^";]+)"?/i.exec(header);
  return m2 ? m2[1].trim() : null;
}

/**
 * True if the error is a caller-initiated abort (the caller passed its own
 * AbortSignal and cancelled the in-flight request). Lets callers silently
 * drop the result of a superseded request without surfacing an error toast
 * or marking state as failed.
 *
 *   } catch (err) {
 *     if (isAbortedError(err)) return;
 *     handleError(err);
 *   }
 */
function isAbortedError(err) {
  return !!(err && (err.data?.code === 'ABORTED' || err.name === 'AbortError'));
}

/**
 * Convert an avatar `uri` (data:, blob:, http(s):) into a File suitable for
 * FormData on web. `expo-image-picker` returns a `data:` URL from
 * FileReader.readAsDataURL; some browsers/extensions reject `fetch()` on
 * data: URLs, so decode those directly instead of round-tripping through
 * fetch().blob(). Falls back to fetch().blob() for blob:/http(s): URIs.
 */
async function avatarUriToFile(uri, fallbackType, fallbackName) {
  const isDataUrl = typeof uri === 'string' && uri.startsWith('data:');
  if (isDataUrl) {
    const commaIdx = uri.indexOf(',');
    const header = commaIdx >= 0 ? uri.slice(0, commaIdx) : '';
    const base64 = uri.slice(commaIdx + 1);
    const mimeMatch = header.match(/data:([^;,]+)/);
    const mime = (mimeMatch && mimeMatch[1]) || fallbackType || 'image/png';
    const isBase64 = /;base64/i.test(header);

    let bytes;
    if (isBase64) {
      const clean = base64.replace(/[^A-Za-z0-9+/=]/g, '');
      const binString = atob(clean);
      bytes = new Uint8Array(binString.length);
      for (let i = 0; i < binString.length; i++) bytes[i] = binString.charCodeAt(i);
    } else {
      const decoded = decodeURIComponent(base64);
      bytes = new Uint8Array(decoded.length);
      for (let i = 0; i < decoded.length; i++) bytes[i] = decoded.charCodeAt(i);
    }
    return new File([bytes], fallbackName, { type: mime });
  }
  const response = await fetch(uri);
  const blob = await response.blob();
  return new File([blob], fallbackName, { type: fallbackType });
}

/**
 * macOS file upload via XHR with manual multipart body.
 * Bypasses RCTFileRequestHandler (which crashes on file:// URIs) by reading
 * the file as base64 through the native MacFilePicker bridge and sending
 * raw bytes in a hand-built multipart/form-data request.
 */
async function macosUploadFile(fileUri, { fieldName, fileName, mimeType, endpoint, extraFields = {} }) {
  const { NativeModules } = require('react-native');
  const base64 = await NativeModules.MacFilePicker.readFileAsBase64(fileUri);

  const authStore = getAuthStore().getState();
  let token = authStore.token;
  if (token && authStore.isTokenExpired()) {
    try { token = await authStore.getValidToken(); }
    catch { authStore.clearSession(); throw new ApiError('Session expired. Please log in again.', 401); }
  }

  const b64Chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';
  const b64Lookup = new Uint8Array(128);
  for (let i = 0; i < b64Chars.length; i++) b64Lookup[b64Chars.charCodeAt(i)] = i;
  const raw = base64.replace(/=+$/, '');
  const outLen = (raw.length * 3) >> 2;
  const fileBytes = new Uint8Array(outLen);
  for (let i = 0, j = 0; i < raw.length; i += 4) {
    const a = b64Lookup[raw.charCodeAt(i)];
    const b = b64Lookup[raw.charCodeAt(i + 1)];
    const c = b64Lookup[raw.charCodeAt(i + 2)];
    const d = b64Lookup[raw.charCodeAt(i + 3)];
    fileBytes[j++] = (a << 2) | (b >> 4);
    if (j < outLen) fileBytes[j++] = ((b & 15) << 4) | (c >> 2);
    if (j < outLen) fileBytes[j++] = ((c & 3) << 6) | d;
  }

  const strToBytes = (s) => {
    const a = new Uint8Array(s.length);
    for (let i = 0; i < s.length; i++) a[i] = s.charCodeAt(i);
    return a;
  };

  const boundary = '----MacOSUpload' + Date.now() + Math.random().toString(36).slice(2);

  let preamble = '';
  for (const [key, val] of Object.entries(extraFields)) {
    preamble += `--${boundary}\r\nContent-Disposition: form-data; name="${key}"\r\n\r\n${val}\r\n`;
  }
  preamble += `--${boundary}\r\nContent-Disposition: form-data; name="${fieldName}"; filename="${encodeURIComponent(fileName)}"\r\nContent-Type: ${mimeType}\r\n\r\n`;
  const epilogue = `\r\n--${boundary}--\r\n`;

  const pre = strToBytes(preamble);
  const post = strToBytes(epilogue);
  const body = new Uint8Array(pre.length + fileBytes.length + post.length);
  body.set(pre, 0);
  body.set(fileBytes, pre.length);
  body.set(post, pre.length + fileBytes.length);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${API_HOST}${endpoint}`);
    xhr.setRequestHeader('Content-Type', `multipart/form-data; boundary=${boundary}`);
    xhr.setRequestHeader('X-Platform', 'macos');
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = () => {
      try { resolve(JSON.parse(xhr.responseText)); }
      catch { reject(new ApiError('Invalid response from server', xhr.status)); }
    };
    xhr.onerror = () => reject(new ApiError('Network error during upload', 0));
    xhr.send(body.buffer);
  });
}

/**
 * Fetch a binary endpoint with auth, returning the raw Response. Callers are
 * responsible for consuming the body. Kept here so API leaf modules can
 * download artifacts without reintroducing the old API monolith.
 */
async function fetchBinaryWithAuth(endpoint, options = {}) {
  await awaitAuthHydration();
  const authStore = getAuthStore().getState();
  let token = authStore.token;
  if (token && authStore.isTokenExpired()) {
    try {
      token = await authStore.getValidToken();
    } catch (_) {
      authStore.clearSession();
      throw new ApiError('Session expired. Please log in again.', 401);
    }
  }

  const headers = {
    'X-Platform': Platform.OS,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
  const { timeoutMs, ...fetchOptions } = options;
  const response = await fetchWithTimeout(`${API_HOST}${endpoint}`, {
    ...fetchOptions,
    credentials: 'include',
    headers,
  }, timeoutMs);

  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const errJson = await response.clone().json();
      if (errJson?.message) message = errJson.message;
    } catch (_) { /* binary endpoint, no JSON body */ }
    throw new ApiError(message, response.status);
  }

  return response;
}

export {
  API_HOST,
  getAuthStore,
  awaitAuthHydration,
  ApiError,
  isAnonAuthError,
  isAbortedError,
  avatarUriToFile,
  endpointRequiresAuthToken,
  shouldRetryTransientRequest,
  fetchWithTimeout,
  fetchWithAuth,
  fetchBinaryWithAuth,
  parseFilenameFromContentDisposition,
  macosUploadFile,
};
